// utils/combatSystem.js

/**
 * Tile and Unit Type Constants
 */
export const TileType = {
  EMPTY: 'empty',
  FULL_COVER: 'full',
  HALF_COVER: 'half'
};

export const UnitType = {
  SOLDIER: 'soldier',
  ENEMY: 'enemy'
};

/**
 * Weapon statistics database
 */
const WEAPONS = {
  assault_rifle: { damage: 2, accuracy: 75 },
  shotgun: { damage: 3, accuracy: 55 },
  sniper: { damage: 4, accuracy: 90 },
  smg: { damage: 1, accuracy: 70 }
};

/**
 * Calculate Manhattan distance between two points
 */
export const distance = (x1, y1, x2, y2) => {
  return Math.abs(x1 - x2) + Math.abs(y1 - y2);
};

/**
 * Get all tiles along a line using Bresenham's algorithm
 */
const getLinePoints = (x1, y1, x2, y2) => {
  const points = [];
  const dx = Math.abs(x2 - x1);
  const dy = Math.abs(y2 - y1);
  const sx = x1 < x2 ? 1 : -1;
  const sy = y1 < y2 ? 1 : -1;
  let err = dx - dy;

  let x = x1;
  let y = y1;

  while (true) {
    points.push({ x, y });
    
    if (x === x2 && y === y2) break;
    
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x += sx;
    }
    if (e2 < dx) {
      err += dx;
      y += sy;
    }
  }
  
  return points;
};

/**
 * Check if there's a clear line of sight between two positions
 * Full cover blocks line of sight
 * Fixed to properly check all tiles along the path
 */
export const hasLineOfSight = (x1, y1, x2, y2, map, tileType = TileType) => {
  const points = getLinePoints(x1, y1, x2, y2);
  
  // Skip first point (shooter) and last point (target)
  for (let i = 1; i < points.length - 1; i++) {
    const { x, y } = points[i];
    
    // Check if point is within map bounds
    if (y < 0 || y >= map.length || x < 0 || x >= map[0].length) {
      return false;
    }
    
    // Full cover blocks line of sight
    if (map[y][x] === tileType.FULL_COVER) {
      return false;
    }
  }
  
  return true;
};

/**
 * Get cover bonus percentage for a position
 */
export const getCoverBonus = (x, y, map, tileType = TileType) => {
  // Check bounds
  if (y < 0 || y >= map.length || x < 0 || x >= map[0].length) {
    return 0;
  }
  
  if (map[y][x] === tileType.FULL_COVER) return 40;
  if (map[y][x] === tileType.HALF_COVER) return 20;
  return 0;
};

/**
 * Get cover type name for display
 */
export const getCoverType = (x, y, map, tileType = TileType) => {
  if (y < 0 || y >= map.length || x < 0 || x >= map[0].length) {
    return 'none';
  }
  
  if (map[y][x] === tileType.FULL_COVER) return 'full cover';
  if (map[y][x] === tileType.HALF_COVER) return 'half cover';
  return 'no cover';
};

/**
 * Get weapon statistics for a given loadout
 */
export const getWeaponStats = (loadout) => {
  const weaponId = loadout?.weapon || 'assault_rifle';
  return WEAPONS[weaponId] || WEAPONS.assault_rifle;
};

/**
 * Calculate hit chance based on weapon, distance, and cover
 */
export const calculateHitChance = (weaponStats, dist, coverBonus) => {
  return Math.max(20, weaponStats.accuracy - dist * 5 - coverBonus);
};

/**
 * Calculate random damage based on weapon damage stat
 */
export const calculateDamage = (weaponStats) => {
  return Math.floor(Math.random() * weaponStats.damage) + 1;
};

/**
 * Execute a shooting action between attacker and target
 * Returns an action result object
 */
export const executeShooting = (attacker, target, targetX, targetY, map, tileType = TileType) => {
  // Check line of sight
  if (!hasLineOfSight(attacker.x, attacker.y, targetX, targetY, map, tileType)) {
    return {
      success: false,
      type: 'no_los',
      message: '⚠️ No line of sight to target!'
    };
  }

  const dist = distance(attacker.x, attacker.y, targetX, targetY);
  const coverBonus = getCoverBonus(targetX, targetY, map, tileType);
  const coverType = getCoverType(targetX, targetY, map, tileType);
  const weaponStats = getWeaponStats(attacker.loadout);
  const hitChance = calculateHitChance(weaponStats, dist, coverBonus);
  const hit = Math.random() * 100 < hitChance;

  if (hit) {
    const damage = calculateDamage(weaponStats);
    const killed = target.hp - damage <= 0;

    return {
      success: true,
      type: 'hit',
      hit: true,
      damage,
      killed,
      hitChance,
      attackerId: attacker.id,
      targetId: target.id,
      coverInfo: coverBonus > 0 ? `${coverType} (-${coverBonus}%)` : null,
      message: `🎯 ${attacker.name} hits ${target.name} for ${damage} damage! (${hitChance}%)`,
      killMessage: killed ? `💀 ${target.name} eliminated!` : null
    };
  } else {
    return {
      success: true,
      type: 'miss',
      hit: false,
      hitChance,
      attackerId: attacker.id,
      targetId: target.id,
      coverInfo: coverBonus > 0 ? `${coverType} (-${coverBonus}%)` : null,
      message: `❌ ${attacker.name} missed! (${hitChance}%)`
    };
  }
};

/**
 * Apply shooting results to units array
 */
export const applyShootingResult = (result, units) => {
  if (!result.success || result.type === 'no_los') {
    return units;
  }

  return units.map(u => {
    // Apply damage to target
    if (u.id === result.targetId && result.hit) {
      return { ...u, hp: Math.max(0, u.hp - result.damage) };
    }
    // Consume attacker's AP and increment kills if target was killed
    if (u.id === result.attackerId) {
      const incrementKills = result.killed ? 1 : 0;
      return { ...u, ap: u.ap - 1, kills: (u.kills || 0) + incrementKills };
    }
    return u;
  });
};

/**
 * Validate if a move is legal
 */
export const canMoveTo = (unit, targetX, targetY, getUnitAt) => {
  const dist = distance(unit.x, unit.y, targetX, targetY);
  const targetOccupied = getUnitAt(targetX, targetY);
  
  return {
    valid: dist <= unit.ap && !targetOccupied,
    distance: dist,
    reason: targetOccupied ? 'occupied' : dist > unit.ap ? 'too_far' : null
  };
};

/**
 * Execute a move action
 */
export const executeMove = (unit, targetX, targetY) => {
  const dist = distance(unit.x, unit.y, targetX, targetY);
  
  return {
    success: true,
    unitId: unit.id,
    newX: targetX,
    newY: targetY,
    apCost: dist,
    message: `${unit.name} moved to (${targetX}, ${targetY})`
  };
};

/**
 * Apply move result to units array
 */
export const applyMoveResult = (result, units) => {
  if (!result.success) return units;
  
  return units.map(u => 
    u.id === result.unitId 
      ? { ...u, x: result.newX, y: result.newY, ap: u.ap - result.apCost }
      : u
  );
};

/**
 * Refresh action points for units of a specific type
 */
export const refreshActionPoints = (units, unitType) => {
  return units.map(u => 
    u.type === unitType ? { ...u, ap: u.maxAp } : u
  );
};

/**
 * Check if any units of a type have action points remaining
 */
export const hasActionsRemaining = (units, unitType) => {
  return units.some(u => u.type === unitType && u.hp > 0 && u.ap > 0);
};

/**
 * Get all alive units of a specific type
 */
export const getAliveUnits = (units, unitType) => {
  return units.filter(u => u.type === unitType && u.hp > 0);
};

/**
 * Check victory conditions
 */
export const checkVictoryConditions = (units) => {
  const aliveSoldiers = getAliveUnits(units, UnitType.SOLDIER);
  const aliveEnemies = getAliveUnits(units, UnitType.ENEMY);
  
  if (aliveSoldiers.length === 0) {
    return { gameOver: true, victory: false, message: '💀 Mission Failed - All soldiers eliminated' };
  }
  
  if (aliveEnemies.length === 0) {
    return { gameOver: true, victory: true, message: '🎉 Mission Success - All hostiles eliminated!' };
  }
  
  return { gameOver: false, victory: false, message: null };
};

/**
 * Create helper functions that are bound to specific map and units
 */
export const createCombatHelpers = (map, units) => {
  const getUnitAt = (x, y) => {
    return units.find(u => u.x === x && u.y === y && u.hp > 0);
  };

  const hasLOS = (x1, y1, x2, y2) => {
    return hasLineOfSight(x1, y1, x2, y2, map, TileType);
  };

  const getCover = (x, y) => {
    return getCoverBonus(x, y, map, TileType);
  };

  return { getUnitAt, hasLineOfSight: hasLOS, getCoverBonus: getCover };
};

const combatSystem = {
  TileType,
  UnitType,
  distance,
  hasLineOfSight,
  getCoverBonus,
  getCoverType,
  getWeaponStats,
  calculateHitChance,
  calculateDamage,
  executeShooting,
  applyShootingResult,
  canMoveTo,
  executeMove,
  applyMoveResult,
  refreshActionPoints,
  hasActionsRemaining,
  getAliveUnits,
  checkVictoryConditions,
  createCombatHelpers
};

export default combatSystem;